// 대상 프레임 상태기계(순수 로직, DOM 없음). 방패 고블린·엘리트, 그리고 자세가 쌓였거나 무방비인 일반 몹을 때리면 보스 바 자리에 작은 프레임이 3초간 뜬다.
// 시각(ms)은 호출자가 넘긴다(테스트 가능). 매 프레임 호출되므로 할당하지 않고 같은 출력 객체를 갱신해 돌려준다.
import { MobKind, MobState } from '../shared/types';

/** 마지막 타격 뒤 프레임이 머무는 시간 */
export const TARGET_HOLD_MS = 3000;
/** 사라질 때 페이드아웃 길이 */
export const TARGET_FADE_MS = 300;
/** 가드 브레이크 표시(꽉 찬 자세 바 + 글자) 길이 */
export const GUARD_BREAK_MS = 800;

/** 프레임이 읽는 몹 뷰 부분(MobsView의 부분집합). posture·guardBroken은 전투 레인 계약 */
export interface TargetMobs {
  readonly state: ArrayLike<number>;
  readonly kind: ArrayLike<number>;
  readonly elite: ArrayLike<number>;
  readonly gen: ArrayLike<number>;
  readonly hp: ArrayLike<number>;
  readonly maxHp: ArrayLike<number>;
  /** 자세 0..1 (COMBAT-FEEL 2.4: 일반 몹도 쌓는다) */
  readonly posture: ArrayLike<number>;
  /** 가드 브레이크 남은 틱(0이면 아님) */
  readonly guardBroken: ArrayLike<number>;
}

export interface TargetFrameOut {
  visible: boolean;
  /** 0..1 (페이드아웃 중 1 미만) */
  opacity: number;
  name: string;
  hpRatio: number;
  /** 화면에 그릴 자세 비율 0..1: 가드 브레이크 표시 중 1, 무방비 동안 0, 그 밖에는 몹의 posture */
  posture: number;
  /** 가드 브레이크 표시 구간(0.8초): 자세 바가 꽉 찬 채 깨진 색으로 보인다 */
  breaking: boolean;
}

/** 지역 이름을 모를 때(마을·R1) 쓰는 몹 이름표. MobKind 순서 */
export const DEFAULT_MOB_NAMES: readonly string[] = ['이끼 고블린', '방패 고블린', '가시 멧돼지', '잿버섯 주술사'];

/** 몹 이름: names(지역 REGIONS[mapId].mobNames, MobKind 순서)에서 고른다. 엘리트는 앞에 '엘리트 ' */
export function mobDisplayName(kind: number, elite: boolean, names: readonly string[] = DEFAULT_MOB_NAMES): string {
  const base = names[kind] ?? names[MobKind.Goblin] ?? DEFAULT_MOB_NAMES[MobKind.Goblin];
  return elite ? '엘리트 ' + base : base;
}

/** 살아 있는 몹 슬롯인가(대상 프레임이 이미 붙은 몹을 계속 따라갈 때) */
export function isLiveMob(m: TargetMobs, i: number): boolean {
  if (!(i >= 0) || i >= m.state.length) return false;
  const st = m.state[i];
  return st !== MobState.Inactive && st !== MobState.Dying;
}

/**
 * 대상 프레임을 새로 띄울 몹인가(살아 있고, 방패 고블린·엘리트이거나 일반 몹이 자세가 쌓였거나 무방비).
 * COMBAT-FEEL 2.4: 일반 몹(이끼 고블린·가시 멧돼지·잿버섯 주술사)도 작은 자세 막대를 갖는다. 자세가 0인 일반 몹은 막대가 빈 채라 띄우지 않는다.
 */
export function isFrameTarget(m: TargetMobs, i: number): boolean {
  if (!isLiveMob(m, i)) return false;
  if (m.kind[i] === MobKind.ShieldGoblin || m.elite[i] === 1) return true;
  return m.posture[i] > 0 || m.guardBroken[i] > 0;
}

export class TargetFrameModel {
  readonly out: TargetFrameOut = { visible: false, opacity: 0, name: '', hpRatio: 0, posture: 0, breaking: false };
  /** 지금 맵의 몹 이름표(HUD가 맵이 바뀔 때 맞춘다). 새 대상을 잡을 때 읽는다 */
  names: readonly string[] = DEFAULT_MOB_NAMES;
  private idx = -1;
  private gen = 0;
  private hitAt = 0;
  /** 몹이 사라진(죽음·슬롯 재사용) 시각, 살아 있으면 -1 */
  private goneAt = -1;
  private breakAt = -1e9;

  /** 몹 타격(Ev.Hit b=0, c=인덱스). 대상으로 받아들였으면 true(체력 바 번쩍임 신호) */
  hit(m: TargetMobs, i: number, now: number): boolean {
    if (!isFrameTarget(m, i)) return false;
    if (i !== this.idx || m.gen[i] !== this.gen || this.goneAt >= 0) this.retarget(m, i);
    this.hitAt = now;
    this.goneAt = -1;
    return true;
  }

  /** 몹 가드 브레이크(Ev.GuardBreak a=0, b=인덱스). 대상으로 받아들였으면 true */
  guardBreak(m: TargetMobs, i: number, now: number): boolean {
    if (!isFrameTarget(m, i)) return false;
    if (i !== this.idx || m.gen[i] !== this.gen || this.goneAt >= 0) this.retarget(m, i);
    this.hitAt = now;
    this.goneAt = -1;
    this.breakAt = now;
    return true;
  }

  reset(): void {
    this.idx = -1;
    this.goneAt = -1;
    this.breakAt = -1e9;
    this.out.visible = false;
    this.out.opacity = 0;
  }

  /** 매 프레임. 보스가 나와 있으면 숨기고 대상도 놓는다 */
  frame(now: number, m: TargetMobs, bossActive: boolean): TargetFrameOut {
    const o = this.out;
    if (bossActive) this.reset();
    if (this.idx < 0) {
      o.visible = false;
      o.opacity = 0;
      return o;
    }
    const i = this.idx;
    // 이미 붙은 몹은 자세가 다시 0으로 빠져도 3초 유지 동안 따라간다(진입 조건 isFrameTarget과 다르다)
    const alive = m.gen[i] === this.gen && isLiveMob(m, i);
    if (alive) {
      this.goneAt = -1;
      o.hpRatio = m.maxHp[i] > 0 ? Math.max(0, Math.min(1, m.hp[i] / m.maxHp[i])) : 0;
      const breaking = now - this.breakAt < GUARD_BREAK_MS;
      const p = m.posture[i];
      o.breaking = breaking;
      o.posture = breaking ? 1 : m.guardBroken[i] > 0 ? 0 : Math.max(0, Math.min(1, Number.isFinite(p) ? p : 0));
    } else {
      if (this.goneAt < 0) this.goneAt = now;
      o.hpRatio = 0;
      o.breaking = false;
    }
    const fadeStart = this.goneAt >= 0 ? this.goneAt : this.hitAt + TARGET_HOLD_MS;
    const t = (now - fadeStart) / TARGET_FADE_MS;
    if (t >= 1) {
      this.reset();
      return o;
    }
    o.visible = true;
    o.opacity = t <= 0 ? 1 : 1 - t;
    return o;
  }

  private retarget(m: TargetMobs, i: number): void {
    this.idx = i;
    this.gen = m.gen[i];
    this.breakAt = -1e9;
    const o = this.out;
    o.name = mobDisplayName(m.kind[i], m.elite[i] === 1, this.names);
  }
}
